/**
 * ============================================================================
 * TEMPLATES STORE - CREATE OPERATIONS
 * ============================================================================
 * 
 * Handles creating new templates via the backend API.
 * 
 * Data Flow:
 * 1. Send template data to API
 * 2. Add new template to local store
 * 3. Return created template
 * 
 * @module templates/api/create
 */

import { apiService } from "@/lib/api-functions/api-service";
import { templatesStore } from "../store";
import type { GlossaryBlueprint } from "@/types-games";
import type { CreateTemplateData, ToastFunction } from "../types";

// ============================================================================
// CREATE OPERATIONS
// ============================================================================

/**
 * Create a new template via API and sync the store.
 * 
 * @param template - The template data to create
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to the created template
 * 
 * @example
 * ```ts
 * const newTemplate = await createTemplate({
 *   game_id: 'game-123',
 *   name: 'Character Template',
 *   type: 'character',
 *   tags: ['npc', 'player'],
 *   description: 'Template for character objects',
 *   data: { sections: [...] },
 *   version: { changes: {}, version: 1 },
 * });
 * ```
 */
export async function createTemplate(
  template: CreateTemplateData,
  toast?: ToastFunction
): Promise<GlossaryBlueprint> {
  try {
    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create via API
    const result = await apiService.createBlueprint(template);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create template');
    }

    const newTemplate = result.data;

    // Add the new template to the store (immutably)
    const currentData = templatesStore.getData();
    const newData = [...currentData, newTemplate];
    templatesStore.setData(newData);

    console.log(`[createTemplate] Created template: ${newTemplate.id} - ${newTemplate.name}`);
    return newTemplate;
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create template';
    console.error('[createTemplate] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
